import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAt,
  endAt,
  where
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, auth, db } from '../config/firebase';

const ORGANIZATIONS_COLLECTION = 'parrotdocs-organizations';
const MEMBERSHIPS_COLLECTION = 'parrotdocs-organization-memberships';
const functions = getFunctions(app);

export const normalizeOrganizationName = (value) => `${value || ''}`
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim()
  .replace(/\s+/g, ' ');

const call = async (name, payload = {}) => {
  const callable = httpsCallable(functions, name, { timeout: 30000 });
  const response = await callable(payload);
  return response.data;
};

const mapSnapshot = (snapshot) => snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));

export const organizationService = {
  async searchOrganizations(searchText, maxResults = 10) {
    if (!auth.currentUser) return [];
    const normalized = normalizeOrganizationName(searchText);
    if (!normalized) return [];
    const snapshot = await getDocs(query(
      collection(db, ORGANIZATIONS_COLLECTION),
      where('status', '==', 'approved'),
      orderBy('normalizedName'),
      startAt(normalized),
      endAt(`${normalized}\uf8ff`),
      limit(Math.max(1, Math.min(20, maxResults)))
    ));
    return mapSnapshot(snapshot);
  },

  async getOrganizationsByIds(organizationIds = []) {
    const ids = Array.from(new Set((organizationIds || []).map((id) => `${id || ''}`.trim()).filter(Boolean)));
    const snapshots = await Promise.all(ids.map((id) => getDoc(doc(db, ORGANIZATIONS_COLLECTION, id))));
    return snapshots.filter((item) => item.exists()).map((item) => ({ id: item.id, ...item.data() }));
  },

  async getUserMemberships(userId = auth.currentUser?.uid) {
    if (!userId) return [];
    const snapshot = await getDocs(query(
      collection(db, MEMBERSHIPS_COLLECTION),
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc')
    ));
    return mapSnapshot(snapshot);
  },

  async getOrganizationMemberships(organizationId) {
    if (!organizationId) return [];
    const snapshot = await getDocs(query(
      collection(db, MEMBERSHIPS_COLLECTION),
      where('organizationId', '==', organizationId),
      orderBy('updatedAt', 'desc')
    ));
    return mapSnapshot(snapshot);
  },

  async getPendingOrganizations() {
    const snapshot = await getDocs(query(
      collection(db, ORGANIZATIONS_COLLECTION),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'asc')
    ));
    return mapSnapshot(snapshot);
  },

  createOrganizationRequest(payload) {
    return call('createOrganizationRequest', payload);
  },
  requestMembership(payload) {
    return call('requestOrganizationMembership', payload);
  },
  reviewOrganization(organizationId, decision) {
    return call('reviewOrganizationRequest', { organizationId, decision });
  },
  reviewMembership(membershipId, decision) {
    return call('reviewOrganizationMembership', { membershipId, decision });
  },
  setMemberRole(membershipId, role) {
    return call('setOrganizationMemberRole', { membershipId, role });
  },
  setActiveOrganization(organizationId) {
    return call('setActiveOrganization', { organizationId });
  },
  leaveOrganization(organizationId) {
    return call('leaveOrganization', { organizationId });
  }
};

export default organizationService;
